import { Directive, Input, TemplateRef, ViewContainerRef, OnDestroy, inject } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Directive({
  selector: '[appFormFieldError]'
})
export class AppFormFieldErrorDirective implements OnDestroy {
  private control: AbstractControl | null = null;
  private subscription: Subscription | null = null;
  private translateService: TranslateService = inject(TranslateService);

  @Input('appFormFieldError') set setControl(control: AbstractControl | null) {
    this.unsubscribe();
    this.control = control;
    this.updateView();
    if (this.control) {
      this.subscription = this.control.statusChanges.subscribe(() => this.updateView());
      this.subscription.add(this.control.valueChanges.subscribe(() => this.updateView()));
      this.subscription.add(this.translateService.onLangChange.subscribe(() => this.updateView()));
    }
  }

  constructor(private templateRef: TemplateRef<any>, private viewContainer: ViewContainerRef) {}

  ngOnDestroy(): void {
    this.unsubscribe();
  }

  private unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  private updateView() {
    this.viewContainer.clear();
    if (!this.control) return;
    if (this.control.dirty && this.control.invalid && this.control.errors) {
      const errors: string[] = this.getErrorMessages(this.control.errors);
      this.viewContainer.createEmbeddedView(this.templateRef, { $implicit: errors });
    }
  }

  private getErrorMessages(errors: ValidationErrors): string[] {
    const messages: string[] = [];
    for (const key of Object.keys(errors)) {
      switch (key) {
        case 'required':
          messages.push(this.translateService.instant('validations.required'));
          break;
        case 'email':
          messages.push(this.translateService.instant('validations.email'));
          break;
        case 'minlength':
          messages.push(
            this.translateService.instant('validations.minlength', {
              length: errors['minlength'].requiredLength
            })
          );
          break;
        case 'maxlength':
          messages.push(
            this.translateService.instant('validations.maxlength', {
              length: errors['maxlength'].requiredLength
            })
          );
          break;
        case 'pattern':
          messages.push(this.translateService.instant('validations.pattern'));
          break;
        case 'numericOnly':
          messages.push(this.translateService.instant('validations.numericOnly'));
          break;
        case 'invalidPhoneNumber':
          messages.push(this.translateService.instant('validations.invalidPhoneNumber'));
          break;
        case 'passwordsMismatch':
          messages.push(this.translateService.instant('validations.passwordsMismatch'));
          break;
        default:
          messages.push(
            errors[key]?.message || this.translateService.instant('validations.invalidField')
          );
      }
    }
    return messages;
  }
}